/**
 * Button Component
 * Follows Stellar brand style guide
 */

import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/utils/classNames';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-stellar-primary hover:bg-stellar-primary-dark',
    'text-white',
    'disabled:bg-bg-tertiary disabled:text-text-disabled'
  ),
  secondary: cn(
    'bg-transparent border-2 border-stellar-primary',
    'text-stellar-primary hover:bg-stellar-primary-light/10',
    'disabled:border-border-default disabled:text-text-disabled'
  ),
  danger: cn(
    'bg-error hover:bg-error-dark',
    'text-white',
    'disabled:bg-bg-tertiary disabled:text-text-disabled'
  ),
  ghost: cn(
    'bg-transparent hover:bg-bg-secondary',
    'text-text-primary',
    'disabled:text-text-disabled'
  ),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2',
          'rounded-lg font-semibold',
          'transition-all duration-base',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-stellar-primary',
          'focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:hover:shadow-none',
          'active:scale-95',
          variantStyles[variant],
          sizeStyles[size],
          variant === 'primary' && !disabled && 'hover:shadow-md',
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
